/* istanbul ignore file */

import Logger from "@next/core-logger"
import React from "react"
import {hydrateRoot} from "react-dom/client"
import {Provider} from "react-redux"
import {createEmotionCache} from "@next/emotion-cache"
import {CacheProvider} from "@emotion/react"

import {getStore, getProps, getState} from "./utils"
import App from "../App"
import executeOnLoad from "../utils/window/executeOnLoad"
import executeOnScroll from "../utils/window/executeOnScroll"
import {getWindow} from "../utils/window"
import "./polyfill"

let isHydrated = false

export const hydrate = () => {
    Logger.debug(`Hydrating Footer`)
    const store = getStore()
    const appProps = getProps()
    const state = getState()
    const cache = createEmotionCache({key: "footer", namespace: true, rtl: appProps.textAlignment === "rtl"})
    isHydrated = true
    const rootElement = document.getElementById("next-footer-entrypoint")
    if (!rootElement) {
        throw new Error("Footer root element not found")
    }
    hydrateRoot(
        rootElement,
        <Provider store={store} serverState={state}>
            <CacheProvider value={cache}>
                <App {...appProps} />
            </CacheProvider>
        </Provider>,
    )
}

// intersection observer callback runs once on load then any time the footer's
// intersection with the viewport changes until it's disconnected
export const intersectionCallback: IntersectionObserverCallback = (entries, obs) => {
    // we're only observing one item (the footer)
    const entry = entries[0]
    // is the footer in the viewport at all?
    const {isIntersecting} = entry

    if (isIntersecting) {
        // footer is in the viewport, queue hydration to run
        obs.disconnect()
        executeOnLoad(hydrate)
    } else {
        // footer is not in viewport, hydrate on scroll/after timeout
        executeOnLoad(() =>
            executeOnScroll(() => {
                // keep the observer running until this callback completes
                // in case the PLP's scrolling manipulation repositions things
                obs.disconnect()
                // make sure we don't run hydration twice, it's a recipe for a bad time
                if (!isHydrated) {
                    hydrate()
                }
            }),
        )
    }
}

export const doHydrate = () => {
    const w = getWindow()
    if (!w) return

    const footer = w.document.getElementById("next-footer-entrypoint")
    if (!footer) return

    if (!w.IntersectionObserver) {
        // if intersection observer not supported, just hydrate
        executeOnLoad(hydrate)
        return
    }

    const observer = new IntersectionObserver(intersectionCallback)
    observer.observe(footer)
}

doHydrate()
